import * as React from 'react';
import { Tag, X } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';
import { Badge } from './badge';

const tagBadgeVariants = cva('gap-1', {
  variants: {
    size: {
      sm: 'text-xs py-0.5 px-2 [&>svg]:size-3',
      default: 'text-xs py-0.5 px-2 [&>svg]:size-3',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export interface TagBadgeProps
  extends Omit<React.ComponentProps<'span'>, 'color'>,
    VariantProps<typeof tagBadgeVariants> {
  name: string;
  color?: string | null;
  removable?: boolean;
  onRemove?: () => void;
}

function TagBadge({
  name,
  color,
  size,
  removable = false,
  onRemove,
  className,
  ...props
}: TagBadgeProps) {
  const backgroundColor = color ? `${color}20` : undefined;
  const textColor = color || undefined;

  return (
    <Badge
      variant="outline"
      className={cn(tagBadgeVariants({ size }), className)}
      style={{
        backgroundColor,
        color: textColor,
        borderColor: color ? `${color}40` : undefined,
      }}
      {...props}
    >
      <Tag />
      <span>{name}</span>
      {removable && onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 hover:opacity-70 transition-opacity"
          aria-label={`Remove tag ${name}`}
        >
          <X className="size-3" />
        </button>
      )}
    </Badge>
  );
}

export { TagBadge, tagBadgeVariants };
